import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Truck, MapPin, Check, Lock } from 'lucide-react';
import Header from './components/Header9';
import Footer from './components/Footer9';
import Button from './components/Button';

const Checkout = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', phone: '',
        address: '', city: '', state: '', pincode: '',
        paymentMethod: 'card',
    });

    const steps = [
        { id: 1, label: 'Shipping', icon: MapPin },
        { id: 2, label: 'Payment', icon: CreditCard },
        { id: 3, label: 'Confirm', icon: Check },
    ];

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const orderItems = [
        { name: 'Chanderi Silk Saree', size: 'M', qty: 1, price: 8999, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Banarasi Kurta Set', size: 'L', qty: 2, price: 6499, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.qty, 0);
    const shipping = 0;
    const total = subtotal + shipping;

    const inputClass = "w-full px-4 py-3 text-sm border border-[#E2E8F0] rounded-none focus:outline-none focus:border-[#2563EB] bg-white text-[#0F172A] placeholder-[#94A3B8]";

    return (
        <div className="w-full min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <div className="pt-20 md:pt-24 pb-8 bg-white border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-0.5 bg-[#2563EB]" />
                        <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium">Secure Checkout</p>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A]">Checkout</h1>
                </div>
            </div>

            {/* Steps */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex items-center justify-center gap-4 sm:gap-8">
                    {steps.map((step, idx) => (
                        <React.Fragment key={step.id}>
                            <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 flex items-center justify-center rounded-none text-sm font-semibold ${
                                    currentStep >= step.id
                                        ? 'bg-[#2563EB] text-white'
                                        : 'bg-[#E2E8F0] text-[#64748B]'
                                }`}>
                                    {currentStep > step.id ? <Check size={16} /> : step.id}
                                </div>
                                <span className={`text-xs sm:text-sm font-medium hidden sm:inline ${
                                    currentStep >= step.id ? 'text-[#2563EB]' : 'text-[#64748B]'
                                }`}>
                                    {step.label}
                                </span>
                            </div>
                            {idx < steps.length - 1 && (
                                <div className={`w-12 sm:w-20 h-0.5 ${currentStep > step.id ? 'bg-[#2563EB]' : 'bg-[#E2E8F0]'}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Form Area */}
                    <div className="lg:col-span-2">
                        {currentStep === 1 && (
                            <div className="bg-white border border-[#E2E8F0] rounded-none p-6 sm:p-8">
                                <h2 className="text-lg font-semibold text-[#0F172A] mb-6 uppercase tracking-wider">Shipping Address</h2>
                                <div className="w-8 h-0.5 bg-[#2563EB] mb-6" />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <input name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} className={inputClass} />
                                    <input name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} className={inputClass} />
                                    <input name="email" type="email" placeholder="Email" value={formData.email} onChange={handleChange} className={inputClass} />
                                    <input name="phone" type="tel" placeholder="Phone" value={formData.phone} onChange={handleChange} className={inputClass} />
                                    <input name="address" placeholder="Street Address" value={formData.address} onChange={handleChange} className={`${inputClass} sm:col-span-2`} />
                                    <input name="city" placeholder="City" value={formData.city} onChange={handleChange} className={inputClass} />
                                    <input name="state" placeholder="State" value={formData.state} onChange={handleChange} className={inputClass} />
                                    <input name="pincode" placeholder="PIN Code" value={formData.pincode} onChange={handleChange} className={inputClass} />
                                </div>
                                <div className="mt-8 flex justify-end">
                                    <Button variant="primary" size="lg" onClick={() => setCurrentStep(2)}>
                                        Continue to Payment
                                    </Button>
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="bg-white border border-[#E2E8F0] rounded-none p-6 sm:p-8">
                                <h2 className="text-lg font-semibold text-[#0F172A] mb-6 uppercase tracking-wider">Payment Method</h2>
                                <div className="w-8 h-0.5 bg-[#2563EB] mb-6" />
                                <div className="space-y-3">
                                    {['card', 'upi', 'cod'].map((method) => (
                                        <label
                                            key={method}
                                            className={`flex items-center gap-4 p-4 border rounded-none cursor-pointer transition-colors duration-300 ${
                                                formData.paymentMethod === method ? 'border-[#2563EB] bg-[#F8FAFC]' : 'border-[#E2E8F0]'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value={method}
                                                checked={formData.paymentMethod === method}
                                                onChange={handleChange}
                                                className="accent-[#2563EB]"
                                            />
                                            <div>
                                                <p className="text-sm font-medium text-[#0F172A]">
                                                    {method === 'card' ? 'Credit/Debit Card' : method === 'upi' ? 'UPI Payment' : 'Cash on Delivery'}
                                                </p>
                                                <p className="text-xs text-[#64748B] font-light">
                                                    {method === 'card' ? 'Visa, Mastercard, RuPay' : method === 'upi' ? 'Google Pay, PhonePe, Paytm' : 'Pay when you receive'}
                                                </p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                <div className="mt-8 flex justify-between">
                                    <Button variant="ghost" onClick={() => setCurrentStep(1)}>Back</Button>
                                    <Button variant="primary" size="lg" onClick={() => setCurrentStep(3)}>
                                        Review Order
                                    </Button>
                                </div>
                            </div>
                        )}

                        {currentStep === 3 && (
                            <div className="bg-white border border-[#E2E8F0] rounded-none p-6 sm:p-8">
                                <h2 className="text-lg font-semibold text-[#0F172A] mb-6 uppercase tracking-wider">Order Review</h2>
                                <div className="w-8 h-0.5 bg-[#2563EB] mb-6" />
                                <div className="space-y-4 mb-6">
                                    {orderItems.map((item, idx) => (
                                        <div key={idx} className="flex gap-4 pb-4 border-b border-[#E2E8F0] last:border-0">
                                            <img src={item.image} alt={item.name} className="w-16 h-20 object-cover rounded-none" />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-[#0F172A]">{item.name}</p>
                                                <p className="text-xs text-[#64748B]">Size: {item.size} | Qty: {item.qty}</p>
                                                <p className="text-sm font-semibold text-[#2563EB] mt-1">{'\u20B9'}{(item.price * item.qty).toLocaleString('en-IN')}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-[#64748B] mb-6">
                                    <Lock size={14} className="text-[#2563EB]" />
                                    <span>Your payment information is encrypted and secure</span>
                                </div>
                                <div className="flex justify-between">
                                    <Button variant="ghost" onClick={() => setCurrentStep(2)}>Back</Button>
                                    <Button variant="primary" size="lg" onClick={() => navigate('/template9/orders')}>
                                        Place Order
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Order Summary Sidebar */}
                    <div>
                        <div className="bg-white border border-[#E2E8F0] rounded-none p-6 sticky top-24">
                            <h3 className="text-lg font-semibold text-[#0F172A] mb-4 uppercase tracking-wider">Summary</h3>
                            <div className="w-8 h-0.5 bg-[#2563EB] mb-4" />
                            <div className="space-y-3 mb-4">
                                {orderItems.map((item, idx) => (
                                    <div key={idx} className="flex justify-between text-sm">
                                        <span className="text-[#64748B] font-light">{item.name} x{item.qty}</span>
                                        <span className="text-[#0F172A] font-medium">{'\u20B9'}{(item.price * item.qty).toLocaleString('en-IN')}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-2 py-4 border-t border-[#E2E8F0]">
                                <div className="flex justify-between text-sm">
                                    <span className="text-[#64748B] font-light">Subtotal</span>
                                    <span className="text-[#0F172A] font-medium">{'\u20B9'}{subtotal.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-[#64748B] font-light">Shipping</span>
                                    <span className="text-green-600 font-medium">Free</span>
                                </div>
                            </div>
                            <div className="flex justify-between text-base font-bold pt-4 border-t border-[#E2E8F0]">
                                <span className="text-[#0F172A]">Total</span>
                                <span className="text-[#2563EB]">{'\u20B9'}{total.toLocaleString('en-IN')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Checkout;
